import React from 'react';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from 'react-router';
import type {MetaDescriptor} from 'react-router';

import './index.css';

import App from '@/app/App';
import {configureApi} from '@/shared/api/serve/axios';
import InitLoader from '@/shared/components/InitLoader';
import {SITE_URL} from '@/shared/config';
import {useLangParam} from '@/shared/hooks/routing/useLangParam';
import {useLocalizedSeoLinks} from '@/shared/hooks/routing/useLocalizedSeoLinks';
import type {AppLanguage} from '@/shared/lib/appLanguageSupports';
import {getAuthToken} from '@/shared/lib/localStorage/authToken';
import {getLocalizedOgLocale} from '@/shared/lib/seo/localizedOgLocales';
import {
  getLanguage,
  localize,
  syncLanguageWithUrl,
  withRequestI18n,
} from '@/shared/services/i18n';
import {logout} from '@/sync/actions/logout';

import type {Route} from './+types/root';
import AppError from './app/components/AppError';

configureApi({
  getApiToken: getAuthToken,
  onUnauthorized: logout,
});

const OG_IMAGE_URL = `${SITE_URL}/images/open_graph_logo.jpg`;

function getLocalizedPageUrl(language: AppLanguage): string {
  const pathname = language === 'en' ? '/' : `/${language}/`;

  return new URL(pathname, SITE_URL).href;
}

export const middleware: Route.MiddlewareFunction[] = [
  ({request}, next) => withRequestI18n(request, next),
];

/**
 * i18next detects the language once, inside `init()`, which the client runs
 * once per document load. Client navigation swaps the URL through the History
 * API, so re-detection has to be driven from the router — here, before the new
 * route renders, so it never paints in the outgoing language.
 */
export const clientMiddleware: Route.ClientMiddlewareFunction[] = [
  async ({request}, next) => {
    await syncLanguageWithUrl(request.url);
    await next();
  },
];

export function meta(): MetaDescriptor[] {
  const language = getLanguage();
  const title = localize('main_page_title');
  const description = localize('main_page_description');
  const ogLocale = getLocalizedOgLocale(language);
  const ogImageAlt = ogLocale
    ? localize('main_page_og_image_alt')
    : title;

  const pageMetadata: MetaDescriptor[] = [
    {
      title,
    },
    {
      name: 'description',
      content: description,
    },
  ];

  const imageMetadata: MetaDescriptor[] = [
    {
      property: 'og:image',
      content: OG_IMAGE_URL,
    },
    {
      property: 'og:image:alt',
      content: ogImageAlt,
    },
  ];

  if (!ogLocale) {
    return [...pageMetadata, ...imageMetadata];
  }

  const ogTitle = localize('main_page_og_title');
  const ogDescription = localize('main_page_og_description');

  return [
    ...pageMetadata,
    {property: 'og:title', content: ogTitle},
    {property: 'og:description', content: ogDescription},
    {property: 'og:url', content: getLocalizedPageUrl(language)},
    {property: 'og:locale', content: ogLocale},
    ...imageMetadata,
    {name: 'twitter:card', content: 'summary_large_image'},
    {name: 'twitter:title', content: ogTitle},
    {name: 'twitter:description', content: ogDescription},
    {name: 'twitter:image', content: OG_IMAGE_URL},
  ];
}

export function Layout({children}: {children: React.ReactNode}) {
  const lang = useLangParam();
  const seoLinks = useLocalizedSeoLinks(lang ?? 'en', SITE_URL);

  return (
    <html lang={lang ?? 'en'}>
      <head>
        <meta charSet="UTF-8" />
        <link rel="icon" sizes="192x192" href="/favicon-192.png" />
        <link rel="icon" sizes="128x128" href="/favicon-128.png" />
        <link rel="icon" sizes="32x32" href="/favicon.png" type="image/png" />
        <link rel="icon" sizes="any" href="/favicon.svg" type="image/svg+xml" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/touchicon-180.png"
        />
        <link rel="apple-touch-icon" href="/touchicon.png" />
        <meta name="theme-color" content="#51aed4" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=5"
        />
        <meta
          name="facebook-domain-verification"
          content="5w28n12dno7irpkzp9clyg5wugr05r"
        />
        <link
          rel="preload"
          href="/fonts/OpenSans-Regular-latin.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="/fonts/OpenSans-Italic-latin.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <Meta />
        <Links />
        {seoLinks.map(({rel, href, hrefLang}) => (
          <link
            key={`${rel}-${hrefLang ?? ''}`}
            rel={rel}
            href={href}
            hrefLang={hrefLang}
          />
        ))}
        <link rel="llms" href="/llms.txt" />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  return (
    <App>
      <Outlet />
    </App>
  );
}

/**
 * React router API
 */
export function HydrateFallback() {
  return <InitLoader />;
}

export function ErrorBoundary() {
  const error = useRouteError();

  return <AppError error={error} />;
}
